import {
  C_INFORMATION,
  C_ON_SURFACE,
  C_ON_SURFACE_LOW_EMPHASIS,
  C_PRIMARY,
  C_SECONDARY_DISABLED,
  C_SURFACE,
  FontSize,
  getFontStyle,
} from "@schaeffler-commerce/design-system";

// Migrated from styles/utility/{margins,flex,flex-grid,fonts,layout}.less.
// These are global utility CLASS rules (bootstrap-era API consumed via
// classnames across the app). The class selectors stay; only the CSS
// authoring moves from LESS to CSS-in-JS. Selector and value formats were
// verified byte-for-byte against the previously compiled bundle CSS.

const GUARD = ":not(.scha__ignore-margins *)";

// margins.less: @margin-sizes/@margin-names pairs (5px base scale, then
// explicit values; gutter = @grid-gutter-width 32px, gutter-half = 16px).
// LESS 5px*0 compiles to unitless 0.
const MARGIN_SCALE: ReadonlyArray<readonly [string, string]> = [
  ["0", "0"],
  ["1", "5px"],
  ["2", "10px"],
  ["3", "20px"],
  ["4", "40px"],
  ["12", "12px"],
  ["15", "15px"],
  ["16", "16px"],
  ["30", "30px"],
  ["auto", "auto"],
  ["gutter", "32px"],
  ["60", "60px"],
  ["gutter-half", "16px"],
];

const marginUtilityStyles: Record<string, Record<string, string>> = {};
for (const [name, value] of MARGIN_SCALE) {
  const important = `${value} !important`;
  // The guard applies to every selector (verified against the compiled
  // bundle CSS): utilities must not apply inside .scha__ignore-margins
  // subtrees (used by e.g. lubrication assistant tabs).
  marginUtilityStyles[`${GUARD}.m-${name}, ${GUARD}.mb-${name}, ${GUARD}.my-${name}`] = {
    marginBottom: important,
  };
  marginUtilityStyles[`${GUARD}.m-${name}, ${GUARD}.mt-${name}, ${GUARD}.my-${name}`] = {
    marginTop: important,
  };
  marginUtilityStyles[`${GUARD}.m-${name}, ${GUARD}.ml-${name}, ${GUARD}.mx-${name}`] = {
    marginLeft: important,
  };
  marginUtilityStyles[`${GUARD}.m-${name}, ${GUARD}.mr-${name}, ${GUARD}.mx-${name}`] = {
    marginRight: important,
  };
  marginUtilityStyles[`${GUARD}.p-${name}, ${GUARD}.pb-${name}, ${GUARD}.py-${name}`] = {
    paddingBottom: important,
  };
  marginUtilityStyles[`${GUARD}.p-${name}, ${GUARD}.pt-${name}, ${GUARD}.py-${name}`] = {
    paddingTop: important,
  };
  marginUtilityStyles[`${GUARD}.p-${name}, ${GUARD}.pl-${name}, ${GUARD}.px-${name}`] = {
    paddingLeft: important,
  };
  marginUtilityStyles[`${GUARD}.p-${name}, ${GUARD}.pr-${name}, ${GUARD}.px-${name}`] = {
    paddingRight: important,
  };
}

// flex-grid.less engine. Breakpoints/gutters/containers mirror
// variables.less exactly; percentage formatting mirrors LESS output
// (4 decimals, trailing zeros trimmed).
const FLEX_GRID_MEDIA = [
  "@media (min-width: 360px)",
  "@media (min-width: 768px)",
  "@media (min-width: 1240px)",
  "@media (min-width: 1536px)",
  "@media (min-width: 1920px)",
];
const FLEX_GRID_GUTTER_HALVES = ["8px", "12px", "12px", "12px", "16px"];
const FLEX_GRID_CONTAINER_PADDING = ["16px", "24px", "16px", "24px", "64px"];
const FLEX_GRID_CONTAINER_WIDTHS = ["1208px", "1488px", "1792px"];
const percentage = (cols: number) => `${parseFloat(((cols * 100) / 12).toFixed(4))}%`;

const colBaseStyles = {
  flex: "0 0 100%",
  maxWidth: "100%",
  paddingLeft: FLEX_GRID_GUTTER_HALVES[0],
  paddingRight: FLEX_GRID_GUTTER_HALVES[0],
  [FLEX_GRID_MEDIA[1]]: {
    paddingLeft: FLEX_GRID_GUTTER_HALVES[1],
    paddingRight: FLEX_GRID_GUTTER_HALVES[1],
  },
  [FLEX_GRID_MEDIA[2]]: {
    paddingLeft: FLEX_GRID_GUTTER_HALVES[2],
    paddingRight: FLEX_GRID_GUTTER_HALVES[2],
  },
  [FLEX_GRID_MEDIA[3]]: {
    paddingLeft: FLEX_GRID_GUTTER_HALVES[3],
    paddingRight: FLEX_GRID_GUTTER_HALVES[3],
  },
  [FLEX_GRID_MEDIA[4]]: {
    paddingLeft: FLEX_GRID_GUTTER_HALVES[4],
    paddingRight: FLEX_GRID_GUTTER_HALVES[4],
  },
};

const columnSizeStyles: Record<string, Record<string, string>> = {};
for (let cols = 1; cols <= 12; cols += 1) {
  columnSizeStyles[`.col-${cols}`] = { flex: `0 0 ${percentage(cols)}`, maxWidth: percentage(cols) };
  columnSizeStyles[`.push-${cols}`] = { marginLeft: percentage(cols) };
}

const infixColumnSizeStyles: Record<string, Record<string, Record<string, string>>> = {};
const INFIXES = ["xs", "sm", "md", "lg", "xl"];
INFIXES.forEach((infix, index) => {
  const mediaStyles: Record<string, Record<string, string>> = {};
  for (let cols = 1; cols <= 12; cols += 1) {
    mediaStyles[`.col-${infix}-${cols}`] = {
      flex: `0 0 ${percentage(cols)}`,
      maxWidth: percentage(cols),
    };
    mediaStyles[`.col-${infix}-push-${cols}`] = { marginLeft: percentage(cols) };
  }
  infixColumnSizeStyles[FLEX_GRID_MEDIA[index]] = mediaStyles;
});

// make-column-padding(): all 72 column selectors share the .col styles.
const allColumnSelectors = INFIXES.concat("")
  .flatMap((infix) => Array.from({ length: 12 }, (_, i) => i + 1).map((n) => `.col-${infix ? `${infix}-` : ""}${n}`))
  .join(", ");

export const utilityStyles = () => ({
  // ── style.less inline utilities (migrated 0068) ──
  ".fullbleed": {
    // .fullbleed mixin from mixins.less
    width: "100vw",
    position: "relative",
    left: "50%",
    right: "50%",
    marginLeft: "-50vw",
    marginRight: "-50vw",
  },
  ".w-100": { width: "100% !important" },
  ".h-100": { height: "100%" },
  ".i-10": { fontSize: "10px" },
  ".i-16": { fontSize: "16px" },
  ".i-20": { fontSize: "20px" },
  ".i-24": { fontSize: "24px" },
  ".i-30": { fontSize: "30px" },
  ".i-50": { fontSize: "50px" },
  ".i-64": { fontSize: "64px" },
  // @icon-margin from variables.less
  ".icon-margin": { marginRight: "8px" },
  ".icon-margin-l": { marginLeft: "8px" },
  ".p-relative": { position: "relative" },
  ".v-hidden": { visibility: "hidden" },
  // @c-surface = grey-95
  ".bg-gray": { backgroundColor: C_SURFACE },
  ".scha__ellipsis-text": {
    whiteSpace: "pre",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  ".forced-ratio-container": {
    position: "relative",
    "& > *": {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
  },
  ".forced-ratio-container--9-16": {
    position: "relative",
    "& > *": {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    paddingTop: "56.25%",
  },
  // ── margins.less ──
  ...marginUtilityStyles,
  // ── layout.less ──
  ".d-block": { display: "block" },
  ".d-inline-block": { display: "inline-block" },
  ".d-inline": { display: "inline" },
  ".disable-horizontal-scroll": { overflowX: "hidden" },
  // ── flex.less ──
  ".d-flex": { display: "flex" },
  ".d-i-flex": { display: "inline-flex" },
  ".d-flex.row:before, .d-i-flex.row:before, .d-flex.row:after, .d-i-flex.row:after": {
    content: '""',
    display: "none",
  },
  ".d-flex.flex-align-end, .d-i-flex.flex-align-end": { alignItems: "flex-end" },
  ".d-flex.flex-align-start, .d-i-flex.flex-align-start": { alignItems: "flex-start" },
  ".d-flex.flex-align-baseline, .d-i-flex.flex-align-baseline": { alignItems: "baseline" },
  ".d-flex.flex-justify-between, .d-i-flex.flex-justify-between": { justifyContent: "space-between" },
  ".d-flex.flex-justify-center, .d-i-flex.flex-justify-center": { justifyContent: "center" },
  ".d-flex.flex-justify-end, .d-i-flex.flex-justify-end": { justifyContent: "flex-end" },
  ".d-flex.flex-align-center, .d-i-flex.flex-align-center": { alignItems: "center" },
  ".d-flex.flex-wrap, .d-i-flex.flex-wrap": { flexWrap: "wrap" },
  ".d-flex.flex-wrap-none, .d-i-flex.flex-wrap-none": { flexWrap: "nowrap !important" },
  ".d-flex.flex-direction-column, .d-i-flex.flex-direction-column": { flexDirection: "column" },
  ".d-flex.flex-direction-row, .d-i-flex.flex-direction-row": { flexDirection: "row" },
  ".d-flex.flex-direction-column-reverse, .d-i-flex.flex-direction-column-reverse": {
    flexDirection: "column-reverse",
  },
  ".d-flex.flex-direction-row-reverse, .d-i-flex.flex-direction-row-reverse": {
    flexDirection: "row-reverse",
  },
  ".flex-align-self-start": { alignSelf: "flex-start" },
  ".flex-none": { flex: "none" },
  ".flex-1": { flex: 1 },
  ".flex-2": { flex: 2 },
  ".flex-1-auto": { flex: "1 1 auto" },
  // ── flex-grid.less ──
  ".container": {
    width: "100%",
    marginLeft: "auto",
    marginRight: "auto",
    paddingLeft: FLEX_GRID_CONTAINER_PADDING[0],
    paddingRight: FLEX_GRID_CONTAINER_PADDING[0],
    [FLEX_GRID_MEDIA[1]]: {
      paddingLeft: FLEX_GRID_CONTAINER_PADDING[1],
      paddingRight: FLEX_GRID_CONTAINER_PADDING[1],
    },
    [FLEX_GRID_MEDIA[2]]: { width: FLEX_GRID_CONTAINER_WIDTHS[0], padding: 0 },
    [FLEX_GRID_MEDIA[3]]: { width: FLEX_GRID_CONTAINER_WIDTHS[1] },
    [FLEX_GRID_MEDIA[4]]: { width: FLEX_GRID_CONTAINER_WIDTHS[2] },
  },
  ".row": {
    display: "flex",
    flexWrap: "wrap",
    marginLeft: `-${FLEX_GRID_GUTTER_HALVES[0]}`,
    marginRight: `-${FLEX_GRID_GUTTER_HALVES[0]}`,
    [FLEX_GRID_MEDIA[1]]: {
      marginLeft: `-${FLEX_GRID_GUTTER_HALVES[1]}`,
      marginRight: `-${FLEX_GRID_GUTTER_HALVES[1]}`,
    },
    [FLEX_GRID_MEDIA[2]]: {
      marginLeft: `-${FLEX_GRID_GUTTER_HALVES[2]}`,
      marginRight: `-${FLEX_GRID_GUTTER_HALVES[2]}`,
    },
    [FLEX_GRID_MEDIA[3]]: {
      marginLeft: `-${FLEX_GRID_GUTTER_HALVES[3]}`,
      marginRight: `-${FLEX_GRID_GUTTER_HALVES[3]}`,
    },
    [FLEX_GRID_MEDIA[4]]: {
      marginLeft: `-${FLEX_GRID_GUTTER_HALVES[4]}`,
      marginRight: `-${FLEX_GRID_GUTTER_HALVES[4]}`,
    },
  },
  ".col": colBaseStyles,
  [allColumnSelectors]: colBaseStyles,
  ...columnSizeStyles,
  ...infixColumnSizeStyles,
  // ── fonts.less ──
  ".f-no-wrap": { whiteSpace: "nowrap" },
  ".f-pre-line": { whiteSpace: "pre-line" },
  ".f-pre": { whiteSpace: "pre" },
  ".f-bold": { fontWeight: "bold" },
  ".f-normal": { fontWeight: "normal" },
  ".f-italic": { fontStyle: "italic" },
  ".f-non-italic": { fontStyle: "normal" },
  ".f-black": { color: "black" },
  ".f-white": { color: "white" },
  ".f-green": { color: C_PRIMARY },
  // #A30F0C is the exact LESS @c-error (@red-30); the design-system C_ERROR
  // token is RED_40 (#CB0B15) — flagged for a token ruling (see task 0064).
  ".f-error-red": { color: "#A30F0C" },
  ".f-info-color": { color: C_INFORMATION },
  ".f-label-color": { color: C_ON_SURFACE_LOW_EMPHASIS },
  ".f-carbon": { color: C_ON_SURFACE },
  ".f-carbon-hover-focus": {
    color: C_ON_SURFACE,
    "&:hover, &:focus": { color: "inherit" },
  },
  ".f-disabled-text": { color: C_SECONDARY_DISABLED },
  ".f-center": { textAlign: "center" },
  ".f-center-imp": { textAlign: "center !important" },
  ".f-right": { textAlign: "right" },
  ".display-small": getFontStyle(FontSize.DisplaySmall),
  ".headline-large": getFontStyle(FontSize.HeadlineLarge),
  ".headline-medium": getFontStyle(FontSize.HeadlineMedium),
  ".headline-small": getFontStyle(FontSize.HeadlineSmall),
  ".title-large": getFontStyle(FontSize.TitleLarge),
  ".title-medium": getFontStyle(FontSize.TitleMedium),
  ".title-small": getFontStyle(FontSize.TitleSmall),
  ".label-large": getFontStyle(FontSize.LabelLarge),
  ".label-medium": getFontStyle(FontSize.LabelMedium),
  ".label-small": getFontStyle(FontSize.LabelSmall),
  ".body-large": getFontStyle(FontSize.BodyLarge),
  ".body-medium": getFontStyle(FontSize.BodyMedium),
  ".body-small": getFontStyle(FontSize.BodySmall),
});
